import React from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import EmployeeProfileSectionCard from "./EmployeeProfileSectionCard";
import { schedulingFieldSx } from "./EmployeeSchedulingPreferences";
import { postRightworkAPI } from "../../utilities/requests";
import { API_post_payroll_external_id } from "../../../../../constants";

export default function EmployeePayrollSection({ selectedTeamMemberData: member, homeStore, fetchStoreTeamMember }) {
  const id = React.useId();
  const stored = member?.payroll_external_id ?? "";
  // The saved value: the prop, or what this form last saved (the member
  // refresh that updates the prop runs in the background).
  const [baseline, setBaseline] = React.useState(stored);
  const [value, setValue] = React.useState(stored);
  const [saving, setSaving] = React.useState(false);
  const [status, setStatus] = React.useState(null); // "saved" | error message

  // A refreshed saved value replaces the field only while it has no edit
  // (or for a different employee), so a refresh landing mid-edit keeps it.
  const lastBaseline = React.useRef(stored);
  const lastMember = React.useRef(member?.id);
  React.useEffect(() => {
    const memberChanged = lastMember.current !== member?.id;
    setValue((current) => (memberChanged || current === lastBaseline.current ? stored : current));
    setBaseline(stored);
    lastBaseline.current = stored;
    lastMember.current = member?.id;
  }, [member?.id, stored]);

  const dirty = value.trim() !== String(baseline).trim();

  const submit = async (event) => {
    event.preventDefault();
    if (!dirty || saving) return;
    setSaving(true);
    setStatus(null);
    try {
      await postRightworkAPI(API_post_payroll_external_id, {
        store_id: homeStore,
        stm_id: member?.id,
        payroll_external_id: value.trim(),
      });
      setBaseline(value.trim());
      lastBaseline.current = value.trim();
      setStatus("saved");
      fetchStoreTeamMember?.(false, member?.id);
    } catch (error) {
      setStatus(error?.response?.data?.error?.message || "Payroll ID could not be saved. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <EmployeeProfileSectionCard header="Payroll" bodyClassName="p-0">
      <form onSubmit={submit} noValidate>
        <div className="px-6 py-5">
          <label htmlFor={id} className="mb-2 block text-sm font-medium text-slate-900">Payroll ID</label>
          <div className="max-w-sm">
            <TextField id={id} fullWidth size="small" placeholder="Not set" sx={schedulingFieldSx} disabled={saving}
              inputProps={{ "aria-describedby": `${id}-help` }}
              value={value} onChange={(event) => { setStatus(null); setValue(event.target.value); }} />
          </div>
          <p id={`${id}-help`} className="mt-2 text-sm leading-5 text-slate-500">
            The ID your payroll provider uses for this employee.
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-3 border-t border-slate-200 px-6 py-4">
          {status === "saved" && <p role="status" className="mr-auto text-sm text-emerald-700">Payroll ID saved.</p>}
          {status && status !== "saved" && <p role="alert" className="mr-auto text-sm text-red-700">{status}</p>}
          <Button type="submit" variant="contained" disableElevation disabled={!dirty || saving}
            sx={{ fontFamily: "inherit", textTransform: "none", borderRadius: "8px", paddingX: "20px", paddingY: "8px", backgroundColor: "#6d28d9", "&:hover": { backgroundColor: "#5b21b6" } }}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </form>
    </EmployeeProfileSectionCard>
  );
}
